import { getCheckoutIntent, getShareableShoppingCart } from "@/api/rest/checkoutApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { fetchProductMetadataWithGraphQL, getActiveCarts } from "@/redux/reducers/checkout/thunk";
import { getAllCarts } from "@/redux/selectors";
import { setAllCarts, setCartItems } from "@/redux/reducers/checkout";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { useApolloClient } from "@apollo/client";
import { AnalyticsTrackingEvent, ShoppingCartItem } from "@brandclub/common-ui";
import { useMachine } from "@xstate/react";
import isEqual from "lodash/isEqual";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkoutUIMachine } from "./checkoutUIMachine";


type CheckoutUIContextType = ReturnType<typeof useMachine<typeof checkoutUIMachine>>;

const CheckoutUIContext = createContext<CheckoutUIContextType | null>(null);

interface CheckoutUIProviderProps {
  children: ReactNode;
}

export const CheckoutUIProvider = ({ children }: CheckoutUIProviderProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchOperationRef = useRef<'idle' | 'in-progress' | 'completed'>('idle');
  // Memoize URL parameters to prevent infinite re-renders
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      checkoutIntentId: searchParams.get("checkoutIntentId"),
      retailerId: parseInt(searchParams.get("retailerId") || "0", 10),
      shareShoppingCartId: searchParams.get("shareShoppingCartId"),
      legacyRetailerSku: searchParams.get("retailerSku"),
      legacyQuantity: parseInt(searchParams.get("quantity") || "1", 10),
      searchParams,
    };
  }, []);

  const { checkoutIntentId, retailerId, shareShoppingCartId, legacyRetailerSku, legacyQuantity } = urlParams;

  // New format: parse multiple items
  const itemsFromUrlParams = useMemo(() => {
    const items: Array<{ sku: string; qty: number }> = [];
    let index = 0;

    // Try new format first
    while (true) {
      const sku = urlParams.searchParams.get(`items[${index}][sku]`);
      const qty = urlParams.searchParams.get(`items[${index}][qty]`);

      if (!sku) break;

      items.push({
        sku,
        qty: parseInt(qty || "1", 10)
      });
      index++;
    }

    // Backwards compatible: single item
    if (items.length === 0 && legacyRetailerSku) {
      items.push({ sku: legacyRetailerSku, qty: legacyQuantity });
    }

    return items;
  }, [urlParams.searchParams, legacyRetailerSku, legacyQuantity]);

  const hasUrlParamsForItems = retailerId && itemsFromUrlParams.length > 0;

  const [trackAction] = useTrackActions();
  const userProfile = useAppSelector((state) => state.userProfile);


  const apolloClient = useApolloClient();
  const carts = useAppSelector(getAllCarts, isEqual);
  const [shareableShoppingCart, setShareableShoppingCart] = useState<{
    items: ShoppingCartItem[];
  } | null>(null);

  // if shareShoppingCartId is present, we need to wait for the shareable shopping cart to load
  const [shareableShoppingCartLoading, setShareableShoppingCartLoading] = useState(shareShoppingCartId ? true : false);
  const [loadingCheckoutItemsFromUrl, setLoadingCheckoutItemsFromUrl] = useState(hasUrlParamsForItems ? true : false);
  const [checkoutItemsFromUrl, setCheckoutItemsFromUrl] = useState<{
    retailerId: number;
    retailerSku: string;
    quantity: number;
  }[] | null>(null);


  useEffect(() => {
    if (shareShoppingCartId) {
      setShareableShoppingCartLoading(true);
      getShareableShoppingCart({ shoppingCartId: shareShoppingCartId }).then(({ items }) => {
        setShareableShoppingCart({ items });
        setShareableShoppingCartLoading(false);
      });
    }
  }, [shareShoppingCartId]);

  useEffect(() => {
    if (hasUrlParamsForItems) {
      setLoadingCheckoutItemsFromUrl(true);
      fetchProductMetadataWithGraphQL({
        apolloClient,
        stacklineSkus: [],
        retailerSkus: itemsFromUrlParams.map(item => item.sku),
        retailerIds: itemsFromUrlParams.map(() => retailerId),
        isAuthenticated: !!userProfile?.userId,
      }).then((products) => {
        // Map fetched products with their quantities from URL params
        const itemsWithQuantities = products.map((product, idx) => ({
          ...product,
          quantity: itemsFromUrlParams[idx]?.qty || 1
        }));
        setCheckoutItemsFromUrl(itemsWithQuantities);
        setLoadingCheckoutItemsFromUrl(false);
      });
    }
  }, [apolloClient, hasUrlParamsForItems, retailerId, itemsFromUrlParams, userProfile?.userId]);

  const itemsToCheckout = useMemo(() => {
    // Priority 1: URL parameters (new or legacy format)
    if (hasUrlParamsForItems) {
      if (loadingCheckoutItemsFromUrl) {
        return []; // Return empty array while loading to prevent null errors
      }
      if (checkoutItemsFromUrl && checkoutItemsFromUrl.length > 0) {
        return checkoutItemsFromUrl;
      }
      // If we have URL params but failed to load items, return empty array
      return [];
    }

    // Priority 2: Shared shopping cart
    if (shareShoppingCartId) {
      return shareableShoppingCart?.items.map((item) => ({
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        quantity: item.quantity,
      })) || [];
    }

    // Priority 3: User's existing cart (fallback)
    if (carts.length > 0 && !loadingCheckoutItemsFromUrl && !hasUrlParamsForItems) {
      let shoppingCartSelected: any;
      if (carts.length === 1) {
        shoppingCartSelected = carts[0];
      } else if (retailerId) {
        shoppingCartSelected = carts.find((cart) => cart.retailerId === retailerId) || carts[0];
      }
      if (!shoppingCartSelected && carts.length > 0) {
        shoppingCartSelected = carts[0];
      }
      if (!shoppingCartSelected) {
        return [];
      }
      return shoppingCartSelected.items.map((item: ShoppingCartItem) => ({
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        quantity: item.quantity,
      }));
    }
    return [];
  },
    [checkoutItemsFromUrl, carts, retailerId, shareShoppingCartId, shareableShoppingCart, loadingCheckoutItemsFromUrl, hasUrlParamsForItems]
  );


  // Create a function to handle navigation that will be passed to the context
  const handleNavigate = (path: string, state?: any) => {
    navigate(path, { state });
  };

  const machine = useMachine(checkoutUIMachine, {
    input: {
      context: {
        navigate: handleNavigate,
        itemsToCheckout,
      },
    },
  });


  const [state, send] = machine;
  const { context } = state;
  // @ts-ignore
  const { checkoutIntent }: { checkoutIntent: CheckoutIntent } = context;
  const cartItems = useAppSelector((state) => state.checkout.cartItems);
  const clearCart = useCallback(() => {
    const retailerIdToClear = checkoutIntent?.retailerId;
    if (!retailerIdToClear) {
      return;
    }
    const newAllCarts = (carts || []).map((cart) =>
      cart.retailerId === retailerIdToClear
        ? { ...cart, items: [], totalItems: 0 }
        : cart
    );
    const newCartItems = (cartItems || []).filter(
      (item) => item.retailerId !== retailerIdToClear
    );
    dispatch(setAllCarts(newAllCarts as any));
    dispatch(setCartItems(newCartItems as any));
    // Optionally sync with backend carts which should already be cleared
    dispatch(getActiveCarts());
  }, [checkoutIntent?.retailerId, carts, cartItems, dispatch]);

  const fetchCheckoutIntent = useCallback(async () => {
    send({
      type: "SET_USER_ID",
      data: { userId: userProfile?.userId },
    });

    // Skip if we're already fetching or have completed; React strict mode will mount this twice
    // and create the checkout intent twice
    if (fetchOperationRef.current !== 'idle' || state.value === 'initial') {
      return;
    }

    // Skip if we already have this checkout intent
    if (checkoutIntent?.checkoutIntentId === checkoutIntentId) {
      fetchOperationRef.current = 'completed';
      return;
    }

    try {
      fetchOperationRef.current = 'in-progress';
      const checkoutIntent = await getCheckoutIntent({
        checkoutIntentId, checkoutItems: itemsToCheckout, retailerId: itemsToCheckout?.[0]?.retailerId,
      });
      if (checkoutIntent) {
        // Track the checkout intent creation
        if (checkoutIntent.createdTime) {
          const createdTime = new Date(checkoutIntent.createdTime);
          const now = new Date();
          const timeDifferenceInSeconds = (now.getTime() - createdTime.getTime()) / 1000;

          if (timeDifferenceInSeconds <= 30) { // 30 seconds to avoid duplicate events
            trackAction(AnalyticsTrackingEvent.CHECKOUT_INTENT_CREATED, {
              checkoutIntentId: checkoutIntent.checkoutIntentId,
              retailerId: checkoutIntent.retailerId,
              checkoutItems: checkoutIntent.items || [],
              cartId: checkoutIntent.cartId,
              itemsCount: checkoutIntent.items?.length || 0
            });
          }
        }

        // Send an event to update the context with the checkout intent data
        send({
          type: "SET_CHECKOUT_INTENT",
          data: {
            checkoutIntent,
            itemsToCheckout,
          }
        });

        // Update the URL params with replace
        navigate(
          `/checkout?checkoutIntentId=${checkoutIntent.checkoutIntentId}`,
          { replace: true }
        );
        fetchOperationRef.current = 'completed';
      }
    } catch (error) {
      console.error("Error fetching checkout intent:", error);
      fetchOperationRef.current = 'idle';
      const serverMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.errorMessage ||
        (error as Error)?.message ||
        "";
      if (serverMessage.toLowerCase().includes("brandclub checkout")) {
        navigate("/mybag");
        return;
      }
      if (
        !checkoutIntentId &&
        (!itemsToCheckout || itemsToCheckout.length === 0)
      ) {
        navigate("/mybag");
      }
    }
  },
    [checkoutIntentId, checkoutIntent, send, navigate, state.value, itemsToCheckout, userProfile?.userId, trackAction]
  );


  useEffect(() => {
    // if userProfile?.userId is present, we need to fetch the checkout intent
    // if checkoutIntent?.userId is not present, we need to fetch the checkout intent
    // if shareShoppingCartId is present, we need to wait for the shareable shopping cart to load
    // if shareShoppingCartId is not present, we need to fetch the checkout intent
    // if we have URL params that should create checkoutItemsFromUrl, we need to wait for itemsToCheckout to be populated
    const shouldWaitForItems = loadingCheckoutItemsFromUrl ||
      (hasUrlParamsForItems && (!checkoutItemsFromUrl || checkoutItemsFromUrl.length === 0));

    // Allow checkout for both signed-in users and guests
    // For signed-in: wait for userProfile and check if checkoutIntent needs updating
    // For guests: proceed if we have items to checkout
    const shouldFetch = (
      (userProfile?.userId && checkoutIntent?.userId !== userProfile?.userId) ||
      (!userProfile?.userId && itemsToCheckout && itemsToCheckout.length > 0 && !checkoutIntent)
    ) && (!shareShoppingCartId || (shareShoppingCartId && !shareableShoppingCartLoading)) &&
      !shouldWaitForItems;

    if (shouldFetch) {
      fetchCheckoutIntent();
    }
  }, [userProfile?.userId, checkoutIntent, fetchCheckoutIntent, shareableShoppingCartLoading, shareShoppingCartId, itemsToCheckout, loadingCheckoutItemsFromUrl, hasUrlParamsForItems, checkoutItemsFromUrl]);

  useEffect(() => {
    send({
      type: "SET_ITEMS_TO_CHECKOUT",
      data: { itemsToCheckout },
    });
  }, [itemsToCheckout, send]);

  useEffect(() => {
    dispatch(getActiveCarts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.userId]);


  return (
    <CheckoutUIContext.Provider value={(() => {
      const [currentState] = machine;
      if (currentState?.context && (currentState as any).context.clearCart !== clearCart) {
        (currentState as any).context.clearCart = clearCart;
      }
      return machine;
    })()}>
      {children}
    </CheckoutUIContext.Provider>
  );
};

export const useCheckoutUI = () => {
  const context = useContext(CheckoutUIContext);
  if (!context) {
    throw new Error("useCheckoutUI must be used within a CheckoutUIProvider");
  }
  return context;
};
